import i18n from '@/i18n';
import { NationalElection, NationalStateSnapshot } from '@/hooks/useNationalBallot';
import { STATE_NAMES } from '@/constants/stateNames';
import {
  classifyElectionType,
  ELECTION_TYPE_LABEL_KEYS,
  type ElectionType,
} from '@/utils/electionType';
import {
  filterOnBallotContests,
  filterOnBallotMeasures,
  withOnBallotCandidates,
} from '@/utils/ballotStatus';

export { STATE_NAMES } from '@/constants/stateNames';

/**
 * Flattened (state, election) entry, shaped to match the signed-in
 * ``Election`` interface so the existing JSX renderer can consume it
 * unchanged. The extra ``state_code`` field is preserved as an escape hatch
 * for any UI that needs to label cards by state.
 *
 * IMPORTANT: keep the field NAMES aligned with the signed-in shape. The
 * existing renderer reads ``election_date``, ``election_name``,
 * ``election_type``, ``guidance_phase``, ``ballot_measures``,
 * ``voting_dates``, and the contest fields camelCase from DW.
 */
export interface SyntheticElection {
  // Synthetic stable id — composite of state + date so React keys don't
  // collide when two states share a primary date (e.g. CA / IA on June 2).
  id: string;
  state_code: string;
  // Signed-in renderer fields:
  election_date: string;
  election_name: string;
  election_type: ElectionType;
  guidance_phase?: string;
  is_past?: boolean;
  contests: NationalElection['contests'];
  ballot_measures: NationalElection['ballotMeasures'];
  voting_dates?: Record<string, unknown>;
}

export interface SyntheticBallotUser {
  // Mirrors the BallotUser shape used by the signed-in renderer, minus the
  // user-specific bits (address, polling_locations) which the national view
  // doesn't have and the renderer treats as optional / falsy-safe.
  id: string;
  next_election_date: string;
  polling_locations: never[];
  elections: SyntheticElection[];
  election_offices: never[];
  source: string;
  timestamp: string;
  updatedAt: string;
}

/**
 * Flatten the per-state national snapshot into one BallotUser-shaped object
 * the signed-in renderer can consume. Each (state, election) pair becomes
 * one SyntheticElection so the existing election-card layout renders one
 * card per state-election, sorted by date.
 */
export const synthesizeBallotUserFromNational = (
  states: NationalStateSnapshot[]
): SyntheticBallotUser => {
  // Today's date in the westernmost US time zone — Hawaii (HST, UTC-10,
  // no DST). Used below to drop elections whose date has already passed.
  // The national snapshot regenerates on a weekly cron, so without this
  // filter a Tuesday election would still appear in the snapshot for the
  // rest of the week — even after polls have closed.
  //
  // Hawaii instead of UTC: a UTC-based cutoff rolls over too early for
  // users on the west coast / Alaska / Hawaii. At 8 PM Pacific on
  // election day, UTC is already on the next calendar date, so a
  // Tuesday election would disappear from the snapshot while polls in
  // Oregon are still open. Using Hawaii (the latest US time zone) as
  // the cutoff means the election stays visible until the date has
  // rolled over for the very last US voter who could still be at the
  // polls. ``en-CA`` is used because its short date format is the
  // YYYY-MM-DD shape that election_date strings already use.
  const todayHawaiiStr = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Pacific/Honolulu',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

  const flat: SyntheticElection[] = [];
  states.forEach((s) => {
    const stateName = STATE_NAMES[s.state_code] ?? s.state_code;
    s.elections.forEach((e) => {
      // Skip past elections. Same-day (>= today-in-Hawaii) stays visible
      // so the polls-still-open voter on the west coast keeps seeing
      // their ballot.
      if (e.date && e.date < todayHawaiiStr) return;
      const type = classifyElectionType(e.description);
      const typeKey = ELECTION_TYPE_LABEL_KEYS[type];
      const typeLabel = typeKey
        ? i18n.t(typeKey, { ns: 'ballot' })
        : e.description || i18n.t('elections.fallbackName', { ns: 'ballot' });
      flat.push({
        id: `${s.state_code}-${e.date}`,
        state_code: s.state_code,
        election_date: e.date,
        election_name: i18n.t('elections.nameWithState', {
          ns: 'ballot',
          state: stateName,
          type: typeLabel,
        }),
        election_type: type,
        guidance_phase: e.guidancePhase,
        is_past: false,
        // The snapshot is a weekly cron over the same feeds, so it can carry a
        // withdrawn measure or candidate for days; apply the status deny-list
        // here, where the payload enters the app.
        contests: filterOnBallotContests(e.contests).map(withOnBallotCandidates),
        ballot_measures: filterOnBallotMeasures(e.ballotMeasures),
        voting_dates: e.voting_dates,
      });
    });
  });
  flat.sort((a, b) => {
    const cmp = (a.election_date ?? '').localeCompare(b.election_date ?? '');
    if (cmp !== 0) return cmp;
    return a.election_name.localeCompare(b.election_name);
  });

  const earliestDate = flat[0]?.election_date ?? '';
  // Use the snapshot_date of the first state doc as a reasonable proxy for
  // "when this national view was generated" — all docs in a snapshot run
  // share the same date.
  const snapshotTimestamp = states[0]?.snapshot_date ?? new Date().toISOString();

  return {
    id: 'national-snapshot',
    next_election_date: earliestDate,
    polling_locations: [],
    elections: flat,
    election_offices: [],
    source: 'VoteMate National Snapshot',
    timestamp: snapshotTimestamp,
    updatedAt: snapshotTimestamp,
  };
};
